import { Fragment, useCallback, useEffect, useMemo, useState } from "react";

import { Link } from "react-router-dom";
import { AnalyticsResourceType, ChannelModel, type CurrencyCode, type InvoiceProduct } from "@doitintl/cmp-models";
import { getCollection } from "@doitintl/models-any";
import CloudDownload from "@mui/icons-material/CloudDownload";
import FilterIcon from "@mui/icons-material/FilterListRounded";
import DownloadIcon from "@mui/icons-material/GetAppRounded";
import OpenNewIcon from "@mui/icons-material/OpenInNewRounded";
import ResetIcon from "@mui/icons-material/SettingsBackupRestoreRounded";
import SyncDisabledIcon from "@mui/icons-material/SyncDisabledRounded";
import SyncIcon from "@mui/icons-material/SyncRounded";
import {
  Badge,
  Box,
  Button,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  SwipeableDrawer,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { makeStyles, withStyles } from "@mui/styles";
import Stack from "@mui/system/Stack";
import { DatePicker } from "@mui/x-date-pickers";
import isEmpty from "lodash/isEmpty";
import orderBy from "lodash/orderBy";
import { DateTime } from "luxon";

import { useApiContext } from "../../../api/context";
import { invoicesText } from "../../../assets/texts";
import { invoicesTxt } from "../../../assets/texts/Billing/invoices";
import Hide from "../../../Components/HideChildren/Hide";
import { useAttributionGroups } from "../../../Components/hooks/cloudAnalytics/attributionGroups/useAttributionGroups";
import { MultiSelect } from "../../../Components/MultiSelect";
import { useErrorSnackbar } from "../../../Components/SharedSnackbar/SharedSnackbar.context";
import { useAuthContext } from "../../../Context/AuthContext";
import { type Entity } from "../../../Context/customer/EntitiesContext";
import { useCustomerContext } from "../../../Context/CustomerContext";
import { ThemeModes } from "../../../muiThemeTypes";
import { consoleErrorWithSentry } from "../../../utils";
import { assetTypeName, formatCurrency, sanitizeDate } from "../../../utils/common";
import { exportCSVFile } from "../../../utils/csv";
import { useFullScreen } from "../../../utils/dialog";
import mixpanel from "../../../utils/mixpanel";
import { type RequiredProperties } from "../../../utils/RequiredProperties";
import { handleDownloadInvoice } from "../downloadInvoice";
import { InvoicesFaqButton } from "../FAQ/InvoicesFAQ";
import { type RowItem, useInvoiceRows } from "../InvoiceDetails/invoiceRow";
import { type Invoice, type InvoiceFilters, type InvoiceStatus, type SetFilters } from "../types";
import { columns } from "./columns";
import { InvoiceInReportsDialog } from "./InvoiceInReportsDialog";
import { LinkedFeeInvoices } from "./LinkedFeeInvoices";
import { NoInvoicesFound } from "./NoInvoicesFound";
import { productIcon, products, standaloneProducts } from "./productConstants";
import { calculateTotalWFees } from "./utils";

const useStyles = makeStyles((theme) => ({
  cardContent: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
  },
  selected: {},
  menu: {},
  centerTableCell: {
    textAlign: "center",
  },
  badge: {
    top: "15%",
    right: -6,
    backgroundColor: theme.palette.text.secondary,
  },
  logo: {
    margin: theme.spacing(0.5, 0, 0, 0.25),
    height: 20,
    width: 20,
  },
  toggleButtonGroup: {
    margin: theme.spacing(1, 0, 0.5),
  },
  productFilterIcon: {
    height: 20,
    width: 20,
  },
}));

const StyledToggleButton = withStyles((theme) => ({
  root: {
    border: `1px solid ${
      theme.palette.mode === ThemeModes.LIGHT ? "rgba(0, 0, 0, 0.23)" : "rgba(255, 255, 255, 0.23)"
    }`,
  },
}))(ToggleButton);

const today = DateTime.utc().startOf("day");
const minDate = DateTime.utc(2011, 1, 1).startOf("year");
const maxDate = today.plus({ year: 1 }).startOf("year");

type Props = {
  invoices: Invoice[];
  setFilters: SetFilters;
  filters: InvoiceFilters;
  isFetching: boolean;
  invoicesProducts: InvoiceProduct[];
  orgEntities: string[] | undefined;
};

export const InvoicesList = ({ invoices, setFilters, filters, isFetching, invoicesProducts, orgEntities }: Props) => {
  const classes = useStyles();
  const api = useApiContext();
  const { isDoitEmployee, currentUser } = useAuthContext({ mustHaveUser: true });

  const { isMobile: matches } = useFullScreen();
  const { customer, entities, isProductOnlyCustomer } = useCustomerContext();
  const [attributionGroups] = useAttributionGroups();
  const [sorting, setSorting] = useState<{ active: string; direction: "asc" | "desc" }>({
    active: "IVDATE",
    direction: "desc",
  });
  const [pageFilter, setPageFilter] = useState<{ page: number; rowsPerPage: number }>({ page: 0, rowsPerPage: 10 });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [channel, setChannel] = useState<boolean>();
  const errorSnackbar = useErrorSnackbar();
  const [inReportsDialogOpen, setInReportsDialogOpen] = useState(false);

  useEffect(() => {
    mixpanel.track("invoices.open");
  }, []);

  useEffect(() => {
    if (isDoitEmployee) {
      try {
        const startTime = DateTime.utc().minus({ minutes: 5 }).toJSDate();
        return getCollection(ChannelModel)
          .where("uid", "==", currentUser.uid)
          .where("complete", "==", false)
          .where("type", "==", "customer.invoices.sync")
          .where("customer", "==", customer.id)
          .where("timestamp", ">=", startTime)
          .orderBy("timestamp", "desc")
          .limit(1)
          .onSnapshot((querySnap) => {
            if (querySnap.empty) {
              setChannel(false);
              return;
            }

            setChannel(true);
          });
      } catch (error) {
        consoleErrorWithSentry(error);
      }
    }
  }, [customer.id, isDoitEmployee, currentUser?.uid]);

  const handleChangePage = (event, page) => {
    setPageFilter({ ...pageFilter, page });
  };

  const handleChangeRowsPerPage = (event) => {
    setPageFilter({ rowsPerPage: event.target.value, page: 0 });
  };

  const handleRequestSort = (active) => () => {
    let direction: "desc" | "asc" = "desc";
    if (sorting.active === active && sorting.direction === "desc") {
      direction = "asc";
    }
    setSorting({ active, direction });
  };

  const handleChangeEntity = ({ target }) => {
    setFilters({
      products: [],
      entityId: target.value ?? undefined,
    });

    setPageFilter({ ...pageFilter, page: 0 });
  };

  const handleChangeDate = (name: "startDate" | "endDate") => (date) => {
    if (date?.isValid) {
      const chosenDate = DateTime.utc(date.year, date.month);
      if (name === "startDate" && filters.endDate < date) {
        setFilters({
          products: [],
          ["startDate"]: chosenDate,
          ["endDate"]: chosenDate,
        });
      } else if (name === "endDate" && filters.startDate > date) {
        setFilters({
          products: [],
          ["startDate"]: chosenDate,
          ["endDate"]: chosenDate,
        });
      } else {
        setFilters({
          products: [],
          [name]: chosenDate,
        });
      }

      setPageFilter({ ...pageFilter, page: 0 });
    }
  };

  const handleChangeStatus = (status: string[]) => {
    setFilters({
      products: [],
      status: status as InvoiceStatus[],
    });

    setPageFilter({ ...pageFilter, page: 0 });
  };

  const handleChangeProducts = (event, newProducts) => {
    let productsToShow = [...newProducts];

    // in case 'standalone' item was selected we need to add to the list both AWS and GCP standalone
    if (productsToShow.includes("standalone")) {
      productsToShow.push(...standaloneProducts);
    } else {
      productsToShow = productsToShow.filter((name) => !standaloneProducts.includes(name));
    }

    setFilters({
      products: productsToShow,
    });

    setPageFilter({ ...pageFilter, page: 0 });
  };

  const { rows } = useInvoiceRows(invoices);

  const linkedInvoices = useMemo(
    () =>
      rows.flatMap((row) => {
        if (row.stripePaymentIntents) {
          return row.stripePaymentIntents
            .filter(
              (payment): payment is RequiredProperties<typeof payment, "linked_invoice"> =>
                !!payment.linked_invoice?.invoice_id
            )
            .map((payment) => payment.linked_invoice.invoice_id);
        }
        return [];
      }),
    [rows]
  );

  const rowsFiltered = useMemo(() => {
    if (filters.startDate < minDate || filters.endDate > maxDate) {
      return [];
    }

    return rows.filter((r) => !linkedInvoices?.includes(r.IVNUM || ""));
  }, [linkedInvoices, rows, filters.startDate, filters.endDate]);

  const currentBalancesByCurrency = useMemo(() => {
    const balancesByCurrency = rowsFiltered.reduce<Partial<Record<CurrencyCode, number>>>((acc, invoiceRowData) => {
      if (!acc[invoiceRowData.CODE]) {
        acc[invoiceRowData.CODE] = 0;
      }
      if (invoiceRowData._STATUS !== "Paid") {
        const currentValue = acc[invoiceRowData.CODE] ?? 0;
        const balance: number = invoiceRowData._BALANCE;
        acc[invoiceRowData.CODE] = currentValue + balance;
      }
      return acc;
    }, {});

    // remove all currencies with 0 net balance
    return Object.keys(balancesByCurrency).reduce<Partial<Record<CurrencyCode, number>>>((acc, key) => {
      if (balancesByCurrency[key] !== 0) {
        acc[key] = balancesByCurrency[key];
      }
      return acc;
    }, {});
  }, [rowsFiltered]);

  const handleRequestSync = useCallback(async () => {
    let sinceMonths = 13;

    const unpaidInvoices = invoices
      .filter((invoice) => !invoice.PAID)
      .sort((a, b) => a.IVDATE.toMillis() - b.IVDATE.toMillis());

    if (unpaidInvoices.length > 0) {
      const firstUnpaidInvoiceDate = DateTime.fromJSDate(unpaidInvoices[0].IVDATE.toDate());
      sinceMonths = Math.max(Math.ceil(DateTime.utc().diff(firstUnpaidInvoiceDate, "months").months) + 1, sinceMonths);
    }

    setChannel(true);
    try {
      await api.request({
        method: "get",
        url: `/v1/customers/${customer.id}/invoices?sinceMonths=${sinceMonths}`,
      });
    } catch (error) {
      consoleErrorWithSentry(error);
      setChannel(false);
    }
  }, [api, customer.id, invoices]);

  const downloadInvoices = useCallback(
    () => () => {
      const headers = {
        invoice: "Invoice Number",
        status: "Status",
        entity: "Entity",
        invoiceDate: "Invoice Date",
        dueDate: "Due Date",
        currency: "Currency",
        total: "Total",
        balance: "Balance",
      };
      const invoiceItems = rowsFiltered.map((rowData) => ({
        invoice: rowData.IVNUM,
        status: rowData._STATUS,
        entity: rowData.CDES,
        invoiceDate: rowData.IVDATE_STRING,
        dueDate: rowData.PAYDATE_STRING,
        currency: rowData.SYMBOL,
        total: rowData.TOTPRICE,
        balance: rowData._BALANCE,
      }));
      const fileTitle = `invoices-${filters.startDate.year}-${filters.endDate.year}`;

      exportCSVFile(headers, invoiceItems, fileTitle);
    },
    [filters.startDate.year, filters.endDate.year, rowsFiltered]
  );

  const showCCFeesColumns =
    linkedInvoices?.length > 0 && linkedInvoices.some((li) => !!invoices.find((i) => i.IVNUM === li));

  // return only the existing products in invoices
  const productFilters = useMemo(() => {
    const uniqProducts = new Set(invoicesProducts);
    return products.filter((product) => {
      if (product.value === "standalone") {
        return uniqProducts.has("google-cloud-standalone") || uniqProducts.has("amazon-web-services-standalone");
      }
      return uniqProducts.has(product.value);
    });
  }, [invoicesProducts]);

  const orgConstraint = useMemo<boolean>(() => !!orgEntities?.length, [orgEntities]);

  const filteredEntities = useMemo<Entity[]>(
    () => (orgEntities?.length ? entities.filter((entity) => orgEntities.includes(entity.id)) : entities),
    [entities, orgEntities]
  );

  const filtersCardContent = (
    <CardContent className={classes.cardContent} sx={{ px: 0 }}>
      <Grid
        container
        spacing={2}
        sx={{
          alignItems: "center",
          justifyContent: "flex-start",
        }}
        size="grow"
      >
        <Grid
          size={{
            xs: 6,
            md: 2,
            lg: 2,
          }}
        >
          <TextField
            size="small"
            select
            name="entity"
            label="Billing Profile"
            margin="dense"
            data-cy="invoices-entity-filter"
            fullWidth
            slotProps={{
              select: {
                value: filters.entityId ?? "",
                onChange: handleChangeEntity,
                displayEmpty: true,
                MenuProps: {
                  className: classes.menu,
                  MenuListProps: {
                    dense: true,
                  },
                },
              },

              inputLabel: {
                shrink: true,
              },
            }}
          >
            {!orgConstraint && <MenuItem value="">Show All</MenuItem>}
            {filteredEntities.map((entity) => (
              <MenuItem
                data-cy={`invoices-entity-filter-option-${entity.id}`}
                key={entity.id}
                value={entity.id}
                classes={{
                  selected: classes.selected,
                }}
              >
                {entity.priorityId} - {entity.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid
          size={{
            xs: 3,
            md: 2,
            lg: 2,
          }}
        >
          <DatePicker
            label="From"
            renderInput={(params) => (
              <TextField size="small" margin="dense" fullWidth data-cy="invoices-start-date-filter" {...params} />
            )}
            value={filters.startDate}
            onChange={handleChangeDate("startDate")}
            inputFormat="LLLL yyyy"
            views={["month", "year"]}
            minDate={minDate}
            maxDate={filters.endDate.endOf("year")}
          />
        </Grid>
        <Grid
          size={{
            xs: 3,
            md: 2,
            lg: 2,
          }}
        >
          <DatePicker
            label="To"
            renderInput={(params) => (
              <TextField size="small" margin="dense" fullWidth data-cy="invoices-end-date-filter" {...params} />
            )}
            value={filters.endDate}
            onChange={handleChangeDate("endDate")}
            inputFormat="LLLL yyyy"
            views={["month", "year"]}
            minDate={filters.startDate.startOf("year")}
            maxDate={maxDate}
          />
        </Grid>
        <Grid
          size={{
            xs: 6,
            md: 2,
            lg: 2,
          }}
        >
          <MultiSelect
            label="Filter by status"
            options={["Open", "Paid", "Partially Paid", "Past Due", "Processing", "Proforma", "Canceled"]}
            selectedOptions={filters.status}
            onChange={handleChangeStatus}
            textFieldProps={{ size: "small", margin: "dense" }}
            data-cy="invoices-status-filter"
          />
        </Grid>
        <Grid
          size={{
            xs: 3,
            md: 2,
            lg: 2,
          }}
        >
          {!isProductOnlyCustomer && (
            <ToggleButtonGroup
              value={filters.products}
              onChange={handleChangeProducts}
              arial-label="products filter"
              size="small"
              className={classes.toggleButtonGroup}
            >
              {productFilters.map((p) => (
                <StyledToggleButton
                  value={p.value}
                  aria-label={p.title}
                  key={p.value}
                  data-cy={`products-filter-${p.value}`}
                >
                  <Tooltip title={p.title}>
                    <img src={productIcon(p.value)} className={classes.productFilterIcon} alt={p.title} />
                  </Tooltip>
                </StyledToggleButton>
              ))}
            </ToggleButtonGroup>
          )}
        </Grid>
      </Grid>
    </CardContent>
  );

  const invoiceAG = useMemo(
    () => attributionGroups.find((ag) => ag.data.type === AnalyticsResourceType.MANAGED),
    [attributionGroups]
  );

  const pageHeaderAction = useMemo(
    () => (
      <>
        <Stack direction="row" divider={<Divider orientation="vertical" flexItem />}>
          {isDoitEmployee && !isProductOnlyCustomer && !!invoiceAG && (
            <Button
              variant="contained"
              onClick={() => {
                setInReportsDialogOpen(true);
              }}
              endIcon={<OpenNewIcon />}
              sx={{ mr: 1 }}
            >
              {invoicesTxt.OPEN_IN_REPORTS}
            </Button>
          )}
          <InvoicesFaqButton />
          <Button
            href={invoicesText.INVOICE_HELP_URL}
            target="_blank"
            variant="text"
            sx={{
              "&:hover": {
                textDecoration: "underline",
                backgroundColor: "transparent",
              },
            }}
            endIcon={<OpenNewIcon sx={{ fontSize: 14, mr: 1 }} />}
          >
            {invoicesText.LEARN_MORE}
          </Button>
        </Stack>

        <Stack
          direction="row"
          sx={{
            justifyContent: "flex-end",
          }}
        >
          <Tooltip key="reset-filters" title="Reset filters">
            <span>
              <IconButton
                aria-label="Reset filters"
                onClick={() => {
                  setFilters(null);
                }}
                size="large"
              >
                <ResetIcon />
              </IconButton>
            </span>
          </Tooltip>

          {matches ? (
            <IconButton
              key="filters"
              aria-label="Open Filters"
              onClick={() => {
                setDrawerOpen(true);
              }}
              size="large"
            >
              <FilterIcon />
            </IconButton>
          ) : (
            <Tooltip key="download-csv" title="Download CSV">
              <span>
                <IconButton
                  aria-label="Back"
                  onClick={downloadInvoices()}
                  disabled={rowsFiltered.length <= 0}
                  size="large"
                >
                  <CloudDownload />
                </IconButton>
              </span>
            </Tooltip>
          )}

          {isDoitEmployee && channel ? (
            <Tooltip key="invoice-sync-disabled" title="Hold on! Invoices are being synced with priority">
              <span>
                <IconButton aria-label="Sync Invoices" disabled size="large">
                  <SyncDisabledIcon />
                </IconButton>
              </span>
            </Tooltip>
          ) : (
            <Tooltip key="invoice-sync" title="Sync invoices with priority">
              <span>
                <IconButton aria-label="Sync Invoices" onClick={handleRequestSync} size="large">
                  <SyncIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Stack>
      </>
    ),
    [
      channel,
      downloadInvoices,
      handleRequestSync,
      invoiceAG,
      isProductOnlyCustomer,
      isDoitEmployee,
      matches,
      rowsFiltered,
      setFilters,
    ]
  );

  const getPage = (rowsFiltered, rowsPerPage: number, page: number) =>
    rowsPerPage > -1 ? rowsFiltered.slice(page * rowsPerPage, rowsPerPage * (page + 1)) : rowsFiltered;

  const balancesSubheader = useMemo(() => {
    if (isEmpty(currentBalancesByCurrency) && !isFetching) {
      return <Typography variant="subtitle1">None</Typography>;
    }
    return Object.keys(currentBalancesByCurrency).map((key, idx) => {
      const printSeparator = (
        <Typography variant="subtitle1" sx={{ mr: 2 }} key={`separator-${key}`}>
          |
        </Typography>
      );
      const printMoney = (
        <Typography variant="subtitle1" sx={{ mr: 2, fontWeight: "medium" }} key={`money-${key}`}>
          {formatCurrency(currentBalancesByCurrency[key], key as CurrencyCode)}
        </Typography>
      );

      if (idx < Object.keys(currentBalancesByCurrency).length - 1) {
        return (
          <Fragment key={`balance-group-${key}`}>
            {printMoney}
            {printSeparator}
          </Fragment>
        );
      } else {
        return printMoney;
      }
    });
  }, [currentBalancesByCurrency, isFetching]);

  return (
    <>
      <CardHeader
        action={pageHeaderAction}
        sx={{ px: 0, py: 1, alignItems: "start" }}
        title="Invoices"
        titleTypographyProps={{ variant: "h1" }}
        subheader={
          <Box
            sx={{
              flexDirection: "row",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Typography variant="body1">Total balance to pay:&nbsp;</Typography>
            {balancesSubheader}
          </Box>
        }
      />
      {matches ? (
        <SwipeableDrawer
          anchor="top"
          open={drawerOpen}
          onClose={() => {
            setDrawerOpen(false);
          }}
          onOpen={() => {
            setDrawerOpen(true);
          }}
          hysteresis={0.25}
        >
          {/* <CardHeader title="Filters" /> */}
          {filtersCardContent}
        </SwipeableDrawer>
      ) : (
        filtersCardContent
      )}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <Hide mdDown>
                <TableCell align="center" padding="checkbox" />
                <TableCell padding="none" />
              </Hide>
              {columns.map(
                (column) =>
                  (!column.doitEmployee || isDoitEmployee) &&
                  (!("ccFees" in column) || showCCFeesColumns) &&
                  (column.hidden ? (
                    <Hide mdDown key={column.id}>
                      <TableCell
                        align={column.align}
                        sortDirection={sorting.active === column.id ? sorting.direction : false}
                      >
                        <Tooltip title={column.tooltip}>
                          <TableSortLabel
                            active={sorting.active === column.id}
                            direction={sorting.direction}
                            onClick={handleRequestSort(column.id)}
                          >
                            {column.label}
                          </TableSortLabel>
                        </Tooltip>
                      </TableCell>
                    </Hide>
                  ) : (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      sortDirection={sorting.active === column.id ? sorting.direction : undefined}
                      size="small"
                    >
                      <Tooltip title={column.tooltip}>
                        <TableSortLabel
                          active={sorting.active === column.id}
                          direction={sorting.direction}
                          onClick={handleRequestSort(column.id)}
                        >
                          {column.label}
                        </TableSortLabel>
                      </Tooltip>
                    </TableCell>
                  ))
              )}
            </TableRow>
          </TableHead>

          {isFetching ? (
            <TableBody>
              <TableRow>
                <TableCell colSpan={9} className={classes.centerTableCell}>
                  <CircularProgress size={32} thickness={5} />
                </TableCell>
              </TableRow>
            </TableBody>
          ) : (
            <>
              <TableBody>
                {getPage(
                  orderBy(rowsFiltered, [sorting.active], [sorting.direction]),
                  pageFilter.rowsPerPage,
                  pageFilter.page
                ).map((row: RowItem) => {
                  const extFile = row.EXTFILES?.[0];
                  return (
                    <TableRow hover key={row.IVNUM}>
                      <Hide mdDown>
                        <TableCell align="center" padding="checkbox">
                          {!!extFile?.url && (
                            <Tooltip title="Download invoice">
                              <span>
                                <IconButton
                                  size="small"
                                  component="button"
                                  onClick={handleDownloadInvoice(extFile, errorSnackbar)}
                                >
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell padding="none">
                          {row.PRODUCTS?.map((type) => {
                            const icon = productIcon(type);
                            if (icon) {
                              return (
                                <Tooltip title={assetTypeName(type)} key={`${row.id}-${type}`}>
                                  <img src={icon} className={classes.logo} alt={type} />
                                </Tooltip>
                              );
                            }
                            return null;
                          })}
                        </TableCell>
                      </Hide>
                      <TableCell align="left">
                        <Typography
                          component={Link}
                          to={`${location.pathname}/${row.entity?.id}/${row.IVNUM}`}
                          color="inherit"
                          variant="body2"
                          style={{ cursor: "pointer" }}
                        >
                          {row.IVNUM}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {row._SHOW_BADGE ? (
                          <Badge variant="dot" classes={{ badge: classes.badge }}>
                            <Typography className={row.statusClassName} variant="body2">
                              {row._ICON} {row._STATUS}
                            </Typography>
                          </Badge>
                        ) : (
                          <Typography className={row.statusClassName} variant="body2">
                            {row._ICON} {row._STATUS}
                          </Typography>
                        )}
                      </TableCell>
                      <Hide mdDown>
                        <TableCell>{row.CDES}</TableCell>
                        <TableCell>
                          {sanitizeDate(DateTime.fromJSDate(row.IVDATE.toDate())).toLocaleString(DateTime.DATE_MED)}
                        </TableCell>
                        <TableCell>
                          {row._STATUS === "Proforma"
                            ? "-"
                            : sanitizeDate(DateTime.fromJSDate(row.PAYDATE.toDate())).toLocaleString(DateTime.DATE_MED)}
                        </TableCell>
                        {isDoitEmployee && (
                          <TableCell>
                            {row.ESTPAYDATE
                              ? sanitizeDate(DateTime.fromJSDate(row.ESTPAYDATE.toDate())).toLocaleString(
                                  DateTime.DATE_MED
                                )
                              : "N/A"}
                          </TableCell>
                        )}
                        {showCCFeesColumns && (
                          <TableCell>
                            <LinkedFeeInvoices invoiceData={row} invoices={invoices} />
                          </TableCell>
                        )}
                        <TableCell align="right">{formatCurrency(row.TOTPRICE ?? 0, row.CODE)}</TableCell>
                        {showCCFeesColumns && (
                          <TableCell align="right">
                            {formatCurrency(calculateTotalWFees(row, invoices), row.CODE)}
                          </TableCell>
                        )}
                      </Hide>
                      <TableCell align="right">
                        {row._STATUS === "Proforma" ? "-" : formatCurrency(row._BALANCE, row.CODE)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
              <TableFooter>
                {rowsFiltered.length > 0 && (
                  <TableRow>
                    <TablePagination
                      count={rowsFiltered.length}
                      rowsPerPage={pageFilter.rowsPerPage}
                      rowsPerPageOptions={[10, 25, { value: -1, label: "All" }]}
                      labelRowsPerPage="Per Page"
                      page={pageFilter.page}
                      slotProps={{
                        actions: {
                          previousButton: {
                            "aria-label": "Previous Page",
                          },
                          nextButton: {
                            "aria-label": "Next Page",
                          },
                        },
                        select: {
                          "aria-label": "Rows per page",
                        },
                      }}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  </TableRow>
                )}
              </TableFooter>
            </>
          )}
        </Table>
      </TableContainer>
      {rowsFiltered.length <= 0 && !isFetching && <NoInvoicesFound />}
      {inReportsDialogOpen && (
        <InvoiceInReportsDialog
          invoiceAG={invoiceAG}
          onClose={() => {
            setInReportsDialogOpen(false);
          }}
        />
      )}
    </>
  );
};
